import xray, { type Params } from '@mail/xray';
import { swDebugLog } from 'serviceWorker/debugLog';
import uuidModule from 'uuid';

/**
 * CLOUDWEB-20961: аналитика Service Worker — транспорт и публичный API.
 *
 * Заменяет самописный `serviceWorker/RADAR.js` (ES5 + CommonJS, 2017) на общий для кодебейза
 * `@mail/xray`. Библиотека умеет в воркер штатно: если в окружении нет ни `XMLHttpRequest`,
 * ни `navigator.sendBeacon` — а в `ServiceWorkerGlobalScope` нет ни того, ни другого, — она
 * уходит на `fetch`.
 *
 * Форма вызова повторяет страничный `sendXray` из `reactApp/utils/ga.ts`: цепочка сегментов
 * склеивается в имя метрики `t`, счётчики едут в `i`. Импортировать ту функцию нельзя — она
 * тянет `lib/xray` (это `window.xray`) и `configHelpers` (это `window.cloudSettings`), в
 * воркере нет ни того, ни другого. Поэтому здесь повторена её обработка имён, а инстанс
 * `@mail/xray` у воркера свой.
 *
 * Ключевое отличие от страницы — `flush()`. `fetch` из воркера не привязан к `event.waitUntil`
 * сам по себе, и браузер вправе снести воркер вместе с незавершённым запросом.
 * Поэтому `send()` запрос запускает, а `flush()` его дожидается, и результат обязан попасть в `waitUntil`.
 */

const IS_TEST = /\.(mini-mail|devmail)\.ru$/.test(self.location.hostname);

/**
 * On-premise-инсталляции не должны стучаться в `xray.mail.ru`. В RADAR это была ветка с
 * `console.log` вместо запроса — сохраняем её один в один (см. `send`).
 *
 * Регулярка намеренно не заякорена, как была в RADAR: `mini-mail.ru` и `devmail.ru` содержат
 * `mail.ru` и потому on-premise не считаются, а `localhost` — считается, и дев-сборка в прод
 * не сорит.
 */
const IS_ON_PREMISE = !/mail\.ru/.test(self.location.hostname);

const RADAR_PREFIX = IS_TEST ? 'test' : '';

const DEFAULT_PROJECT = 'cloud';

/**
 * Потолок ожидания отправки. Запрос к `xray.mail.ru` режется блокировщиками и в норме
 * отваливается сразу, но зависший (никогда не отвечающий) запрос без этого удерживал бы воркер
 * до принудительного убийства браузером — вместе с показом пуша, который его ждёт.
 */
const FLUSH_TIMEOUT_MS = 3000;

/**
 * Серверные лимиты парсера радаров. Выписаны в исходниках `@mail/xray` (`_validateParams`)
 * прямо из его конфига: `max_type_len = 64` для `p + '_' + t` и для `p + '_' + t + '_' + <ключ i>`,
 * `max_key_size = 32` для самого ключа. Превышение xray не отправляет вовсе — бросает и пишет
 * в консоль, — поэтому режем заранее и с отметкой в debug-логе.
 */
const MAX_TYPE_LENGTH = 64;
const MAX_KEY_LENGTH = 32;

export interface SwXrayOptions {
    /** Проект. По умолчанию `cloud`; продуктовые события едут в `cloud-web-analytics`. */
    p?: string;
    dwh?: Record<string, unknown>;
    /**
     * Сохранять ли в Graphite записи с dwh-переменными. Подразумевается при `dwh_enabled_fields`.
     * Без `dwh` бессмысленно — там нечего раскладывать.
     */
    dwh_enabled?: boolean;
    /**
     * Цепочки dwh-полей, которые бэкенд раскладывает в Graphite отдельным поддеревом:
     * `[['campaign_id']]`. Каждое поле умножает число серий на количество своих значений,
     * а серии в Graphite остаются навсегда — брать только категориальные поля с малым набором
     * значений, и только те, которых нет в имени метрики.
     */
    dwh_enabled_fields?: string[][];
    rlog?: string;
    rlog_message?: Record<string, unknown>;
}

/** Всё, кроме латиницы, цифр и дефиса — разделитель пути в Graphite или мусор в имени метрики. */
const normalizeSymbols = (name: string): string => name.replace(/[^a-zA-Z0-9-]/gi, '-');

const getPrefixLength = (p: string): number => `${p ? `${p}_` : ''}${RADAR_PREFIX ? `${RADAR_PREFIX}_` : ''}`.length;

const buildType = (names: string | Array<string | undefined>, p: string): string => {
    const chain = Array.isArray(names) ? names : [names];
    const type = chain
        .filter((segment): segment is string => Boolean(segment))
        .map((segment) => {
            const normalized = normalizeSymbols(segment);

            // Молчаливое искажение имени стоило дня отладки: радар уходил, отвечал 200 и оседал
            // в соседней ветке Graphite, мимо селектора Метиды. Чаще всего ловится подчёркивание
            // внутри сегмента — оно склеивает сегменты цепочки, а не живёт внутри одного
            // (`'pwa_push'` → `pwa-push`; правильно `['pwa', 'push']`).
            if (normalized !== segment) {
                swDebugLog({ event: 'radar-segment-normalized', segment, normalized });
            }

            return normalized;
        })
        .join('_');
    const limit = Math.max(0, MAX_TYPE_LENGTH - getPrefixLength(p));

    if (type.length > limit) {
        swDebugLog({ event: 'radar-type-too-long', type, length: type.length, limit });

        return type.slice(0, limit);
    }

    return type;
};

const buildIntervals = (iData: Record<string, number>, type: string, p: string): Record<string, number> => {
    const limit = Math.min(MAX_KEY_LENGTH, Math.max(0, MAX_TYPE_LENGTH - getPrefixLength(p) - `${type}_`.length));

    return Object.keys(iData).reduce<Record<string, number>>((result, key) => {
        // Места под ключ не осталось вовсе — имя метрики уже съело весь лимит. Обрезка дала бы
        // пустой ключ, а он проскакивает и валидацию xray (там пустая строка falsy) и уезжает
        // как `i=:1`. Лучше потерять счётчик, чем прислать мусор.
        if (limit <= 0) {
            swDebugLog({ event: 'radar-key-dropped', key, type, reason: 'no-room' });

            return result;
        }

        if (key.length > limit) {
            swDebugLog({ event: 'radar-key-too-long', key, length: key.length, limit });
        }

        result[key.slice(0, limit)] = iData[key];

        return result;
    }, {});
};

let pending: Promise<unknown>[] = [];

/**
 * Единственная точка, где мы видим запрос xray: библиотека вызывает `hitmanPatchedFetch`
 * вместо глобального `fetch`, если он задан в конфиге очереди.
 *
 * Так мы получаем промис отправки, не подменяя `self.fetch` глобально: подмена задела бы и
 * `PushApiFetch`, и `hub_link`-запросы на пути показа пуша, то есть ради метрики рисковала бы
 * самим пушем.
 */
const trackedFetch = (input: string, init?: RequestInit): Promise<Response> => {
    const request = fetch(input, init);

    // Ошибку глотаем в отдельной ветке: сам `request` возвращается xray, который вешает на него
    // свой `catch`, а в очередь ожидания кладём заведомо неотклоняемый промис — иначе `flush`
    // падал бы на первом же заблокированном запросе.
    pending.push(
        request.catch((error) => {
            // eslint-disable-next-line no-console
            console.error('[sw-xray] send failed', error);
        })
    );

    return request;
};

// Конфиг очереди ставим ДО `setConfig`: у `@mail/xray` он one-shot — первый вызов задаёт
// поля целиком, а последующие могут только добавлять отсутствующие (`setConfig` внутри себя
// тоже конфигурирует очередь, то есть после него часть полей уже занята значениями по
// умолчанию и молча игнорируется).
xray.setQueueConfig({
    hitmanPatchedFetch: trackedFetch,
});

xray.setConfig({
    xrayRadarUrl: 'https://xray.mail.ru',
    // Библиотека сама доклеит `_`: имя метрики собирается как `<radarPrefix>_<t>`.
    radarPrefix: RADAR_PREFIX,
    pgid: uuidModule('worker'),
    defaultParams: {
        p: DEFAULT_PROJECT,
        v: 1,
    },
});

/** Дождаться запросов, ушедших с прошлого флаша. */
const awaitPending = (): Promise<void> => {
    const snapshot = pending;

    pending = [];

    if (!snapshot.length) {
        return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
        // Таймер гасим по факту отправки: висящий `setTimeout` держал бы событийный цикл
        // воркера дольше, чем нужно, и мешал бы ему нормально завершиться.
        const timer = setTimeout(resolve, FLUSH_TIMEOUT_MS);

        Promise.all(snapshot).then(() => {
            clearTimeout(timer);
            resolve();
        });
    });
};

export const swXray = {
    /**
     * Отправить радар. Запрос уходит сразу, но никого не ждёт — дожидается его `flush()`.
     *
     * `sendImmediately`, а не `send`: у `send` отправку запускает секундный таймер очереди, а в
     * воркере ждать секунду нечего — он живёт ровно столько, сколько держит `waitUntil`.
     *
     * @param names сегменты имени метрики; склеиваются через `_` в `t`
     * @param iData счётчики-подметрики: `{ 'ключ': значение }`
     */
    send(names: string | Array<string | undefined>, iData?: Record<string, number> | null, options: SwXrayOptions = {}): void {
        const p = options.p || DEFAULT_PROJECT;
        const type = buildType(names, p);
        // Именно по длине, а не по наличию: пустой массив truthy, и `dwh_enabled_fields: []`
        // включил бы разбивку, которой нечего раскладывать.
        const hasDwhFields = Boolean(options.dwh_enabled_fields?.length);

        const params: Params = {
            ...(p === DEFAULT_PROJECT ? {} : { p }),
            ...(iData ? { i: buildIntervals(iData, type, p) } : {}),
            ...(options.dwh ? { dwh: options.dwh } : {}),
            ...(options.dwh_enabled || hasDwhFields ? { dwh_enabled: options.dwh_enabled || hasDwhFields } : {}),
            ...(hasDwhFields ? { dwh_enabled_fields: options.dwh_enabled_fields } : {}),
            // rlog без rlog_message xray выбрасывает — шлём только парой.
            ...(options.rlog && options.rlog_message ? { rlog: options.rlog, rlog_message: options.rlog_message } : {}),
        };

        // Логируем то, что уходит, а не то, что собирались отправить: `t` уже нормализован и с префиксом, `i` обрезан под лимиты
        swDebugLog({
            event: 'radar-sent',
            t: type,
            p,
            i: params.i,
            dwh: params.dwh,
            dwh_enabled_fields: params.dwh_enabled_fields,
            ...(IS_ON_PREMISE ? { skipped: 'on-premise' } : {}),
        });

        // On-premise никуда не ходим, но радар показываем в консоли воркера — так вела себя и
        // ветка `ON_PREMISE` в RADAR, и без неё отладка аналитики на своей инсталляции слепая.
        if (IS_ON_PREMISE) {
            // eslint-disable-next-line no-console
            console.log('[sw-xray]', type, params);

            return;
        }

        xray.sendImmediately(type, params);
    },

    /**
     * Дождаться, пока уйдут запросы, отправленные с прошлого флаша.
     *
     * Результат обязан попасть в `event.waitUntil` — напрямую или через `await` внутри уже
     * обёрнутого в него хендлера. Без этого воркер снесут вместе с незавершённым запросом,
     * и радар не доедет.
     *
     * Забыть `flush()` не опасно: радар всё равно отправлен, просто его никто не подождёт.
     */
    flush: awaitPending,
};
